import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock } from "vue"

import DataTable from "@/components/layout/DataTable.vue";
import {useI18n} from "vue3-i18n";
import {computed} from "vue";
import {DocumentCategoryHelper, Guid} from "@/types";
import {DocumentPolicyHelper} from "@/types/documentPolicy";
import {useDocumentService, useFavoriteDocumentService} from "@/inversify.config";
import {Document} from "@/types/entities/document";
import {notifyError, notifySuccess} from "@/notify";
import {SucceededOrNotResponse} from "@/types/responses";

// eslint-disable-next-line

export default /*#__PURE__*/_defineComponent({
  __name: 'DocumentsTable',
  props: {
    documents: {},
    searchValue: {},
    isLoading: { type: Boolean }
  },
  emits: ["documentsUpdated"],
  setup(__props: any, { emit: __emit }) {

const emit = __emit;

// eslint-disable-next-line no-undef
const props = __props

// eslint-disable-next-line
const {t, getLocale} = useI18n();

const documentService = useDocumentService();
const favoriteDocumentService = useFavoriteDocumentService();

const documentPolicies = computed(() => DocumentPolicyHelper.policiesValueAndNames());

const documents = computed(() => {
  if (props.documents.length == 0) {
    return [];
  }

  const categoryLabelsByName = new Map<string, string>(
    DocumentCategoryHelper.categoriesNameAndLabel().map((c): [string, string] => [c.name, c.label])
  );

  return props.documents
    .map((x: Document) => {
      return {
        id: x.id,
        policy: documentPolicies.value.filter((y) => y.name == x.policy)[0]?.label,
        category: categoryLabelsByName.get(x.category ?? "") ?? "",
        categoryColor: DocumentCategoryHelper.getCategoryColorClass(x.category),
        date: x.date ?? "",
        name: x.name != null ? x.name.extractFileName() : "",
        actions: {
          download: x.documentPath,
          favorite: {
            isInIt: x.favoriteDocumentId != null
          },
          update: x.canEditOrDeleteDocument
            ? {name: "coproperty.children.editDocument", params: {id: x.id},}
            : null,
          delete: x.canEditOrDeleteDocument
        },
      };
    }) as Document[];
});

async function deleteDocument(document: any) {
  if (document?.id == null || !Guid.isValid(document.id ?? ""))
    return;

  let confirmDelete = confirm(t("document.deleteConfirmation"));

  if (!confirmDelete) return;

  let result: SucceededOrNotResponse = await documentService.deleteDocument(document.id);
  if (result?.succeeded) {
    notifySuccess(t("document.deleteSuccess"));
    emit("documentsUpdated")
  } else {
    notifyError(t("document.deleteError"));
  }
}

async function addToFavorite(documentId: string) {
  if (!Guid.isValid(documentId ?? "") || documentId == null)
    return;

  let result: SucceededOrNotResponse = await favoriteDocumentService.addToFavorite(documentId);
  if (result?.succeeded) {
    notifySuccess(t("document.addFavoriteSuccess"));
    emit("documentsUpdated")
  } else {
    notifyError(t("document.addFavoriteError"));
  }
}

async function removeFromFavorite(documentId: string) {
  if (!Guid.isValid(documentId ?? "") || documentId == null) return;

  let favDocumentId = props.documents.find((x: Document) => x.id?.toString() == documentId)?.favoriteDocumentId;
  let result: SucceededOrNotResponse = await favoriteDocumentService.removeFromFavorite(favDocumentId ?? "");
  if (result?.succeeded) {
    notifySuccess(t("document.removeFavoriteSuccess"));
    emit("documentsUpdated")
  } else {
    notifyError(t("document.removeFavoriteError"));
  }
}

const documentHeader = [
  {
    text: t("global.title"),
    value: "name",
    sortable: true,
    width: 200,
  },
  {
    text: t("document.policy"),
    value: "policy",
    sortable: true,
    width: 150,
  },
  {
    text: t("document.category"),
    value: "category",
    sortable: true,
    width: 150,
  },
  {
    text: t("global.date"),
    value: "date",
    sortable: true,
    width: 120,
  },
  {
    text: t("global.table.actions"),
    value: "actions",
    width: 150,
  }
]

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(DataTable, {
    headers: documentHeader,
    "is-loading": _ctx.isLoading,
    items: documents.value,
    "search-value": _ctx.searchValue,
    onDelete: deleteDocument,
    onAddFavorite: addToFavorite,
    onRemoveFavorite: removeFromFavorite
  }, null, 8, ["is-loading", "items", "search-value"]))
}
}

})